import { useEffect, useState } from "react";
import { onValue, ref } from "firebase/database";
import { rtdb } from "../firebase";
import {
  WIKI_LIVE_PATH,
  WIKI_LIVE_STALE_MS,
  type WikiLiveEntity,
  type WikiLivePark,
} from "../config/themeparksWiki";

export interface UseParkWaitTimesResult {
  /** Map of ThemeParks.wiki child-entity id -> live snapshot. */
  entities: Record<string, WikiLiveEntity>;
  fetchedAt: number | null;
  stale: boolean;
  loading: boolean;
}

/**
 * Subscribe to the cached live wait-time data for a park, keyed by the park's
 * ThemeParks.wiki entity id. Returns the whole child-entities map so callers
 * can badge a park's attraction list without a separate read per child.
 */
export function useParkWaitTimes(
  parkExternalId: string | null | undefined,
): UseParkWaitTimesResult {
  const [park, setPark] = useState<WikiLivePark | null>(null);
  const [loading, setLoading] = useState<boolean>(!!parkExternalId);

  useEffect(() => {
    if (!parkExternalId) {
      setPark(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const node = ref(rtdb, `${WIKI_LIVE_PATH}/${parkExternalId}`);
    const unsubscribe = onValue(
      node,
      (snap) => {
        setPark((snap.val() as WikiLivePark | null) ?? null);
        setLoading(false);
      },
      () => {
        setPark(null);
        setLoading(false);
      },
    );
    return unsubscribe;
  }, [parkExternalId]);

  const fetchedAt = typeof park?.fetchedAt === "number" ? park.fetchedAt : null;
  return {
    entities: park?.entities ?? {},
    fetchedAt,
    stale: fetchedAt != null && Date.now() - fetchedAt > WIKI_LIVE_STALE_MS,
    loading,
  };
}
